import React, { useState } from "react";
import { ShieldCheck, Flag, Award, ArrowRight, CheckCircle2, Loader2, BadgePercent, Shield, Siren } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useT } from "@/lib/i18n";
import { api } from "@/lib/api";
import HeroesDialog from "@/components/site/HeroesDialog";

const FLAG_BG = `${process.env.REACT_APP_BACKEND_URL}/api/uploads/veterans_flag.jpg`;
const OWNER_IMG = `${process.env.REACT_APP_BACKEND_URL}/api/uploads/veteran_owner.jpg`;

function ItemChip({ value, current, onChange, icon: Icon, label }) {
  const active = current === value;
  return (
    <button
      type="button"
      data-testid={`vet-claim-item-${value}`}
      onClick={() => onChange(value)}
      className={[
        "flex-1 inline-flex items-center justify-center gap-2 rounded-full border h-11 px-4 text-sm font-semibold transition-all",
        active
          ? "bg-brand-navy text-white border-brand-navy shadow-sm"
          : "bg-white text-brand-navy border-slate-300 hover:border-brand-navy/60",
      ].join(" ")}
    >
      <Icon className="h-4 w-4" />
      {label}
    </button>
  );
}

export default function VeteransHonor() {
  const { t } = useT();
  const [itemType, setItemType] = useState("flag");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [status, setStatus] = useState("idle"); // idle | submitting | success | error

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !email.trim()) return;
    setStatus("submitting");
    try {
      await api.post("/veteran-claims", {
        name: name.trim(),
        email: email.trim(),
        phone: phone.trim(),
        item_type: itemType,
      });
      setStatus("success");
    } catch (err) {
      setStatus("error");
    }
  };

  const resetForm = () => {
    setName("");
    setEmail("");
    setPhone("");
    setItemType("flag");
    setStatus("idle");
  };

  return (
    <section
      id="veterans"
      data-testid="veterans-section"
      className="relative py-24 lg:py-32 overflow-hidden"
    >
      {/* Background waving US flag */}
      <div className="absolute inset-0 z-0">
        <img
          src={FLAG_BG}
          alt="Waving US flag"
          className="w-full h-full object-cover" loading="lazy" decoding="async" />
        <div className="absolute inset-0 bg-gradient-to-r from-brand-navy via-brand-navy/90 to-brand-navy/70" />
        <div className="absolute inset-0 bg-gradient-to-t from-brand-navy/85 via-transparent to-brand-navy/40" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-start">
          {/* LEFT: Story + owner photo */}
          <div className="lg:col-span-7 text-white">
            <div
              data-testid="vet-badge"
              className="inline-flex items-center gap-2 rounded-full border border-amber-300/40 bg-amber-500/10 backdrop-blur px-3.5 py-1.5 mb-6"
            >
              <ShieldCheck className="h-4 w-4 text-amber-300" />
              <span className="text-xs sm:text-sm font-bold uppercase tracking-[0.18em] text-amber-200">
                {t("vet.badge")}
              </span>
            </div>

            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-amber-300">
              {t("vet.eyebrow")}
            </p>

            <h2 className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05]">
              {t("vet.title1")}
              <span className="block text-amber-300">{t("vet.title2")}</span>
            </h2>

            <p className="mt-6 text-base sm:text-lg text-slate-200 leading-relaxed max-w-xl">
              {t("vet.body")}
            </p>

            {/* Owner photo */}
            <figure
              data-testid="vet-owner-photo"
              className="mt-10 inline-flex flex-col sm:flex-row items-center gap-5 sm:gap-6 rounded-3xl bg-white/10 backdrop-blur-md border border-white/20 p-4 sm:p-5 max-w-xl"
            >
              <img
                src={OWNER_IMG}
                alt={t("vet.owner.alt")}
                className="h-28 w-28 sm:h-32 sm:w-32 rounded-2xl object-cover ring-4 ring-amber-300/40 shadow-xl" loading="lazy" decoding="async" />
              <figcaption className="text-center sm:text-left">
                <p className="text-amber-200 text-[11px] font-bold uppercase tracking-[0.18em]">
                  {t("hero.badge")}
                </p>
                <p className="mt-1.5 text-base sm:text-lg font-semibold text-white leading-snug">
                  {t("vet.owner.caption")}
                </p>
              </figcaption>
            </figure>

            <p
              data-testid="vet-fineprint"
              className="mt-6 text-xs sm:text-sm text-slate-300/80 max-w-xl"
            >
              {t("vet.fineprint")}
            </p>
          </div>

          {/* RIGHT: Benefit cards + claim form */}
          <div className="lg:col-span-5 grid gap-4">
            <article
              data-testid="vet-benefit-flag"
              className="relative rounded-3xl bg-white/95 backdrop-blur-xl border border-white/40 shadow-2xl p-6"
            >
              <div className="flex items-start gap-4">
                <div className="h-12 w-12 shrink-0 rounded-2xl bg-red-600 text-white grid place-items-center shadow-lg">
                  <Flag className="h-5 w-5" />
                </div>
                <div className="flex-1">
                  <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-red-600">
                    Free of charge
                  </p>
                  <h3 className="mt-1 font-display text-lg font-bold text-brand-navy">
                    {t("vet.benefit1.title")}
                  </h3>
                  <p className="mt-1.5 text-sm text-slate-600 leading-relaxed">
                    {t("vet.benefit1.body")}
                  </p>
                </div>
              </div>
              <div className="us-stripe h-1 rounded-full mt-4" />
            </article>

            <article
              data-testid="vet-benefit-uniform"
              className="relative rounded-3xl bg-white/95 backdrop-blur-xl border border-white/40 shadow-2xl p-6"
            >
              <div className="flex items-start gap-4">
                <div className="h-12 w-12 shrink-0 rounded-2xl bg-brand-navy text-amber-300 grid place-items-center shadow-lg">
                  <Award className="h-5 w-5" />
                </div>
                <div className="flex-1">
                  <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-brand-navy">
                    Free of charge
                  </p>
                  <h3 className="mt-1 font-display text-lg font-bold text-brand-navy">
                    {t("vet.benefit2.title")}
                  </h3>
                  <p className="mt-1.5 text-sm text-slate-600 leading-relaxed">
                    {t("vet.benefit2.body")}
                  </p>
                </div>
              </div>
              <div className="us-stripe h-1 rounded-full mt-4" />
            </article>

            {/* Compact claim form */}
            <div
              data-testid="vet-claim-card"
              className="rounded-3xl bg-white shadow-2xl border border-white/40 p-6 sm:p-7"
            >
              {status === "success" ? (
                <div className="text-center py-3" data-testid="vet-claim-success">
                  <div className="mx-auto h-12 w-12 rounded-full bg-emerald-100 grid place-items-center">
                    <CheckCircle2 className="h-6 w-6 text-emerald-600" />
                  </div>
                  <h3 className="mt-4 font-display text-xl font-bold text-brand-navy">
                    {t("vet.claim.success.title")}
                  </h3>
                  <p className="mt-2 text-sm text-slate-600 leading-relaxed">
                    {t("vet.claim.success.body")}
                  </p>
                  <button
                    type="button"
                    onClick={resetForm}
                    data-testid="vet-claim-another"
                    className="mt-4 text-sm font-semibold text-brand-navy underline underline-offset-4 hover:text-amber-600"
                  >
                    {t("vet.claim.success.another")}
                  </button>
                </div>
              ) : (
                <form onSubmit={submit} className="space-y-4">
                  <div>
                    <h3 className="font-display text-xl font-bold text-brand-navy">
                      {t("vet.claim.heading")}
                    </h3>
                    <p className="mt-1 text-sm text-slate-600 leading-relaxed">
                      {t("vet.claim.desc")}
                    </p>
                  </div>

                  <div>
                    <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                      {t("vet.claim.item")}
                    </Label>
                    <div className="mt-2 flex gap-2">
                      <ItemChip
                        value="flag"
                        current={itemType}
                        onChange={setItemType}
                        icon={Flag}
                        label={t("vet.claim.item.flag")}
                      />
                      <ItemChip
                        value="uniform"
                        current={itemType}
                        onChange={setItemType}
                        icon={Award}
                        label={t("vet.claim.item.uniform")}
                      />
                    </div>
                  </div>

                  <div className="grid gap-3">
                    <div>
                      <Label htmlFor="vet-name" className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                        {t("vet.claim.name")}
                      </Label>
                      <Input
                        id="vet-name"
                        data-testid="vet-claim-name"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        required
                        className="mt-1.5 h-11"
                        autoComplete="name"
                      />
                    </div>
                    <div className="grid sm:grid-cols-2 gap-3">
                      <div>
                        <Label htmlFor="vet-email" className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                          {t("vet.claim.email")}
                        </Label>
                        <Input
                          id="vet-email"
                          data-testid="vet-claim-email"
                          type="email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          required
                          className="mt-1.5 h-11"
                          autoComplete="email"
                        />
                      </div>
                      <div>
                        <Label htmlFor="vet-phone" className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                          {t("vet.claim.phone")}
                        </Label>
                        <Input
                          id="vet-phone"
                          data-testid="vet-claim-phone"
                          value={phone}
                          onChange={(e) => setPhone(e.target.value)}
                          className="mt-1.5 h-11"
                          autoComplete="tel"
                        />
                      </div>
                    </div>
                  </div>

                  <Button
                    type="submit"
                    data-testid="vet-claim-submit"
                    disabled={status === "submitting"}
                    className="w-full rounded-full bg-amber-500 hover:bg-amber-400 text-brand-navy h-12 text-base font-bold gap-2 shadow-[0_14px_30px_-12px_rgba(245,158,11,0.7)]"
                  >
                    {status === "submitting" ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" /> {t("vet.claim.submitting")}
                      </>
                    ) : (
                      <>
                        {t("vet.claim.submit")} <ArrowRight className="h-4 w-4" />
                      </>
                    )}
                  </Button>
                  {status === "error" && (
                    <p data-testid="vet-claim-error" className="text-sm text-red-600 text-center">
                      {t("vet.claim.error")}
                    </p>
                  )}
                </form>
              )}
            </div>

            {/* Heroes 15% off — Active Duty & First Responders */}
            <article
              data-testid="heroes-card"
              className="relative rounded-3xl p-6 sm:p-7 overflow-hidden bg-gradient-to-br from-brand-navy via-brand-navy to-brand-blue text-white shadow-2xl border border-white/10"
            >
              <div className="flex items-start gap-4">
                <div className="h-12 w-12 shrink-0 rounded-2xl bg-amber-500 text-brand-navy grid place-items-center shadow-lg">
                  <BadgePercent className="h-5 w-5" />
                </div>
                <div className="flex-1">
                  <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-amber-300">
                    {t("heroes.cardTag")}
                  </p>
                  <h3 className="mt-1 font-display text-lg font-bold">
                    {t("heroes.cardTitle")}
                  </h3>
                  <p className="mt-1.5 text-sm text-slate-200 leading-relaxed">
                    {t("heroes.cardBody")}
                  </p>
                </div>
              </div>
              <div className="mt-5 flex items-center gap-2 flex-wrap">
                <span className="inline-flex items-center gap-1.5 text-xs font-semibold rounded-full bg-white/10 backdrop-blur px-3 py-1 border border-white/15">
                  <Shield className="h-3.5 w-3.5" /> {t("heroes.dialog.role.active_duty")}
                </span>
                <span className="inline-flex items-center gap-1.5 text-xs font-semibold rounded-full bg-white/10 backdrop-blur px-3 py-1 border border-white/15">
                  <Siren className="h-3.5 w-3.5" /> {t("heroes.dialog.role.first_responder")}
                </span>
              </div>
              <HeroesDialog
                testIdPrefix="heroes"
                trigger={
                  <Button
                    data-testid="heroes-cta"
                    className="mt-5 w-full rounded-full bg-amber-500 hover:bg-amber-400 text-brand-navy h-11 font-bold gap-2 shadow-[0_14px_30px_-12px_rgba(245,158,11,0.7)]"
                  >
                    <BadgePercent className="h-4 w-4" /> {t("heroes.cardCta")}
                  </Button>
                }
              />
            </article>
          </div>
        </div>
      </div>
    </section>
  );
}
